import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomers, useCustomerAddresses, useProducts, type Product } from "@/lib/db";
import { useCreateOrder, type PaymentMethod } from "@/lib/orders";
import { getDeliverySettings, listDeliveryDates, formatDateDe, formatDateIso } from "@/lib/calendar";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

interface CartLine { product_id: string; qty: number; }

export default function NeueBestellungDrawer({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const create = useCreateOrder();

  // Kunde wählen (Suche)
  const [search, setSearch] = useState("");
  const { data: customers = [] } = useCustomers(search);
  const [customerId, setCustomerId] = useState<string>("");

  // Adresse wählen (abhängig vom Kunden)
  const { data: addresses = [] } = useCustomerAddresses(customerId || undefined);
  const [addressId, setAddressId] = useState<string>("");
  useEffect(() => {
    if (addresses.length && !addresses.some((a) => a.id === addressId)) {
      setAddressId(addresses.find((a) => a.is_default)?.id ?? addresses[0].id);
    }
    if (!addresses.length) setAddressId("");
  }, [addresses, addressId]);

  // Produkte
  const { data: products = [] } = useProducts({ onlyActive: true });
  const [lines, setLines] = useState<CartLine[]>([]);
  const productMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  function addLine(p: Product) {
    setLines((cur) => {
      const ex = cur.find((l) => l.product_id === p.id);
      return ex
        ? cur.map((l) => l.product_id === p.id ? { ...l, qty: l.qty + 1 } : l)
        : [...cur, { product_id: p.id, qty: 1 }];
    });
  }
  function setQty(id: string, qty: number) {
    setLines((cur) =>
      qty <= 0 ? cur.filter((l) => l.product_id !== id) : cur.map((l) => l.product_id === id ? { ...l, qty } : l)
    );
  }

  // Liefertag
  const [dates, setDates] = useState<Date[]>([]);
  const [deliveryDate, setDeliveryDate] = useState("");
  useEffect(() => {
    getDeliverySettings().then((s) => {
      const list = listDeliveryDates(new Date(), s, 10);
      setDates(list);
      setDeliveryDate(formatDateIso(list[0]));
    });
  }, []);

  const [payment, setPayment] = useState<PaymentMethod>("cash");
  const [source, setSource] = useState<"phone" | "app">("phone");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const totalCents = lines.reduce(
    (a, l) => a + l.qty * (productMap.get(l.product_id)?.price_cents ?? 0), 0
  );

  async function onSave() {
    setErr(null);
    if (!customerId) return setErr("Bitte Kunden auswählen.");
    if (!addressId) return setErr("Bitte Adresse auswählen.");
    if (!lines.length) return setErr("Mindestens ein Produkt hinzufügen.");
    if (!deliveryDate) return setErr("Bitte Liefertag wählen.");
    try {
      const order = await create.mutateAsync({
        customer_id: customerId,
        address_id: addressId,
        delivery_date: deliveryDate,
        payment_method: payment,
        source,
        notes: notes.trim() || undefined,
        items: lines.map((l) => {
          const p = productMap.get(l.product_id)!;
          return { product_id: l.product_id, qty: l.qty, unit_price_cents: p.price_cents };
        }),
      });
      onClose();
      nav(`/backoffice/bestellungen/${order.id}`);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-hof-900/40 flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-hof-50 w-full max-w-2xl h-full overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-hof-900">Neue Bestellung</h2>
          <button onClick={onClose} className="btn-ghost p-2">✕</button>
        </div>

        {/* Kunde */}
        <section className="card space-y-2">
          <label className="label">Kunde *</label>
          <input className="input" placeholder="Name suchen…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search.length >= 2 && (
            <div className="max-h-48 overflow-y-auto divide-y divide-hof-100 rounded-xl border border-hof-100">
              {customers.map((c) => (
                <button key={c.id} onClick={() => { setCustomerId(c.id); setSearch(c.display_name); }}
                  className={`w-full text-left p-3 hover:bg-hof-100 ${customerId === c.id ? "bg-wiese-50" : ""}`}>
                  <div className="font-medium">{c.display_name}</div>
                  <div className="text-xs text-hof-500">{c.type === "gewerblich" ? "Gewerblich" : "Privat"} · {c.phone ?? "—"}</div>
                </button>
              ))}
            </div>
          )}
          {customerId && (
            <div className="text-sm text-wiese-700">✓ Ausgewählt: {customers.find((c) => c.id === customerId)?.display_name}</div>
          )}
        </section>

        {/* Adresse */}
        {customerId && (
          <section className="card space-y-2">
            <label className="label">Lieferadresse *</label>
            {addresses.length === 0 ? (
              <p className="text-sm text-hof-600">Dieser Kunde hat noch keine Adresse — bitte erst im Kundenstamm anlegen.</p>
            ) : (
              <div className="space-y-1">
                {addresses.map((a) => (
                  <label key={a.id} className={`block p-2 rounded-lg border cursor-pointer ${
                    addressId === a.id ? "border-wiese-500 bg-wiese-50" : "border-hof-200 hover:bg-hof-100"
                  }`}>
                    <input type="radio" checked={addressId === a.id} onChange={() => setAddressId(a.id)} className="mr-2" />
                    {a.label ?? "Adresse"} — {a.street}, {a.zip} {a.city}
                  </label>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Produkte */}
        <section className="card space-y-2">
          <label className="label">Artikel *</label>
          <div className="flex flex-wrap gap-2">
            {products.map((p) => (
              <button key={p.id} onClick={() => addLine(p)} className="btn-secondary text-sm py-2 px-3">
                + {p.name} ({fmt.format(p.price_cents / 100)})
              </button>
            ))}
          </div>
          {lines.length > 0 && (
            <div className="divide-y divide-hof-100 mt-3">
              {lines.map((l) => {
                const p = productMap.get(l.product_id);
                return (
                  <div key={l.product_id} className="py-2 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{p?.name}</div>
                      <div className="text-xs text-hof-500">{fmt.format((p?.price_cents ?? 0) / 100)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setQty(l.product_id, l.qty - 1)} className="btn-secondary p-2 w-9">−</button>
                      <span className="w-8 text-center font-mono">{l.qty}</span>
                      <button onClick={() => setQty(l.product_id, l.qty + 1)} className="btn-secondary p-2 w-9">+</button>
                    </div>
                    <div className="w-20 text-right font-medium">{fmt.format((p?.price_cents ?? 0) * l.qty / 100)}</div>
                  </div>
                );
              })}
              <div className="pt-2 flex justify-between font-bold">
                <span>Summe</span>
                <span className="text-wiese-700">{fmt.format(totalCents / 100)}</span>
              </div>
            </div>
          )}
        </section>

        {/* Liefertag */}
        <section className="card space-y-2">
          <label className="label">Liefertag *</label>
          <select className="input" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}>
            {dates.map((d) => <option key={formatDateIso(d)} value={formatDateIso(d)}>{formatDateDe(d)}</option>)}
          </select>
        </section>

        {/* Zahlart + Quelle */}
        <section className="card grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Zahlart</label>
            <select className="input" value={payment} onChange={(e) => setPayment(e.target.value as PaymentMethod)}>
              <option value="cash">Bar bei Lieferung</option>
              <option value="invoice">Auf Rechnung</option>
              <option value="sepa">SEPA-Lastschrift</option>
              <option value="stripe">Karte (Stripe)</option>
              <option value="paypal">PayPal</option>
            </select>
          </div>
          <div>
            <label className="label">Quelle</label>
            <select className="input" value={source} onChange={(e) => setSource(e.target.value as "phone" | "app")}>
              <option value="phone">Telefon</option>
              <option value="app">App (manuell)</option>
            </select>
          </div>
        </section>

        <section className="card">
          <label className="label">Notiz</label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </section>

        {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}

        <button onClick={onSave} disabled={create.isPending} className="btn-primary w-full py-4 text-lg">
          {create.isPending ? "Anlegen…" : `Bestellung anlegen (${fmt.format(totalCents / 100)})`}
        </button>
      </div>
    </div>
  );
}
